import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Building2, ShieldCheck, FileText, Zap, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-track-6/artifacts/ylx5ssnx_Insite%20Logo%204.png';

// NYC Skyline image from Unsplash (free to use)
const NYC_IMAGE_URL = 'https://images.unsplash.com/photo-1534430480872-3498386e7856?q=80&w=2070&auto=format&fit=crop';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await register(formData);
      toast.success('Account created successfully!');
      navigate('/');  // Go to city selector
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 'Registration failed';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Building2, text: 'Track compliance across your entire portfolio' },
    { icon: ShieldCheck, text: 'Real-time violation alerts and monitoring' },
    { icon: FileText, text: 'Professional compliance reports on demand' },
    { icon: Zap, text: 'AI-powered risk analysis and insights' },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 flex" data-testid="register-page">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 py-12 lg:px-16 relative">
        {/* Background subtle gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 to-zinc-950 z-0" />
        
        <div className="relative z-10 max-w-md mx-auto w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center mb-10" data-testid="register-logo">
            <img src={LOGO_URL} alt="Pivotal Insite360" className="h-12 w-auto" />
          </Link>

          {/* Welcome Text */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Create Your Account</h1>
            <p className="text-zinc-400">Get started with building compliance monitoring</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-300 text-sm">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="input-dark w-full h-11"
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300 text-sm">Work Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="input-dark w-full h-11"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-zinc-300 text-sm">Company Name <span className="text-zinc-500">(Optional)</span></Label>
              <Input
                id="company"
                type="text"
                placeholder="Your Company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="input-dark w-full h-11"
                data-testid="register-company-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300 text-sm">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  className="input-dark w-full pr-10 h-11"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-zinc-500">Minimum 6 characters</p>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="btn-primary w-full h-11 text-base mt-2"
              data-testid="register-submit-btn"
            >
              {loading ? <span className="spinner mr-2" /> : null}
              Create Account
            </Button>
          </form>

          <p className="text-center text-zinc-400 mt-8">
            Already have an account?{' '}
            <Link to="/login" className="text-indigo-400 hover:text-indigo-300 font-medium" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - NYC Image with Overlay */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${NYC_IMAGE_URL})` }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-transparent to-zinc-950/40" />
        
        {/* Content Overlay */}
        <div className="absolute inset-0 flex flex-col justify-center p-12">
          {/* Benefits List */}
          <div className="glass-card rounded-xl p-8 backdrop-blur-md bg-zinc-900/60 border border-white/10 max-w-lg">
            <h2 className="text-2xl font-bold text-white mb-2">
              Why Professionals Choose Insite360
            </h2>
            <p className="text-zinc-400 mb-6">
              Join thousands of property managers, developers, and compliance professionals
            </p>
            
            {/* Benefits */}
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-4 h-4 text-indigo-400" />
                  </div>
                  <span className="text-zinc-200 text-sm pt-1">{benefit.text}</span>
                </div>
              ))}
            </div>
            
            {/* Trust Signal */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-400" />
                <span className="text-sm text-zinc-300">Free to start • No credit card required</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
